import { RelatedPerson } from "fhir/r4";
import { FunctionComponent, useEffect, useRef, useState } from "react";
import { Button, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

/**
 * Has to be used inside a Form component. Use configuration displayed below to enable validation.
 * 
 * <Form noValidate validated={validated}>
 * 
 * @param props properties for the component.
 * @returns the rendered Elements.
 */
const RelatedPersonForm: FunctionComponent<{
    submitTrigger?: boolean,
    resetTrigger?: boolean,
    displayButtons?: boolean,
    relatedPersonSubmit: (relatedPerson: RelatedPerson) => void
}> = (props) => {
    const { t } = useTranslation();
    const submitRelatedPersonRef = useRef<() => void>(() => undefined);
    submitRelatedPersonRef.current = generateAndSubmitPatient;

    useEffect(() => {
        if (props.resetTrigger) {
            onReset();
        }
    }, [props.resetTrigger]);

    useEffect(() => {
        if (props.submitTrigger) {
            submitRelatedPersonRef.current();
        }
    }, [props.submitTrigger]);

    const [form, setForm] = useState({
        name: '',
        surname: '',
        phone: '',
        gender: 'unknown',
        relation: '',
    });

    const handleChange = (event: { target: { name: any; value: any; }; }) => {
        setForm({ ...form, [event.target.name]: event.target.value });
    }

    const onSubmit = (event: any) => {
        const formtv = event.currentTarget;
        if (formtv.checkValidity() === true) {
            generateAndSubmitPatient();
        }
    }

    function generateAndSubmitPatient() {
        if (props.relatedPersonSubmit) {

            const relatedPerson = {
                "resourceType": "RelatedPerson",
                "identifier": [
                    {
                        "system": "https://demo.fyrstain.com/Aceso",
                        "value": "123456789",
                    }
                ],
                "active": true,
                "relationship": [
                    {
                      "coding": [
                        {
                          "code": form.relation,
                          "system": "https://mos.esante.gouv.fr/NOS/TRE_R260-HL7RoleClass/FHIR/TRE-R260-HL7RoleClass"
                        }
                      ]
                    }
                  ],
                  "name": [
                    {
                      "use": "official",
                      "family": form.name,
                      "given": [
                        form.surname
                      ]
                    }
                  ],
                  "telecom": [
                    {
                      "system": "phone",
                      "value": form.phone
                    }
                  ],
                  "gender": form.gender
            } as RelatedPerson;

            props.relatedPersonSubmit(relatedPerson);
        }
    }

    const onReset = () => {
        setForm({
            name: '',
            surname: '',
            phone: '',
            gender: 'unknown',
            relation: '',
        });
    }

    return <><Form.Group
        className="col"
    >
        <Form.Label>{t("forms.common.name")} :</Form.Label>
        <Form.Control
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            className="form-control"
            required />
        <Form.Control.Feedback type="invalid">
            {t("forms.relatedPerson.invalidName")}
        </Form.Control.Feedback>
    </Form.Group>
        <Form.Group className="col">
            <Form.Label>{t("forms.common.firstName")} :</Form.Label>
            <Form.Control
                type="text"
                name="surname"
                value={form.surname}
                onChange={handleChange}
                className="form-control"
                required />
            <Form.Control.Feedback type="invalid">
                {t("forms.relatedPerson.invalidFirstName")}
            </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="col col-sm-6">
            <Form.Label>{t("forms.common.gender")} :</Form.Label>
            <Form.Select
                className="form-control"
                name="gender"
                value={form.gender}
                onChange={handleChange}
            >
                {(["unknown", "female", "male", "other"] as const).map((gender) => <option key={gender} value={gender}>{t(`forms.common.genderOptions.${gender}`)}</option>)}
            </Form.Select>
        </Form.Group>

        <Form.Group
            className="col"
        >
            <Form.Label>{t("forms.common.phone")} :</Form.Label>
            <Form.Control
                type="phone"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                className="form-control"
                required />
            <Form.Control.Feedback type="invalid">
                {t("forms.relatedPerson.invalidPhone")}
            </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="col col-sm-6">
            <Form.Label>{t("forms.relatedPerson.relation")} :</Form.Label>
            <Form.Select
                className="form-control"
                name="relation"
                value={form.relation}
                onChange={handleChange}
            >
                <option value="">{t("forms.common.choose")}</option>
                {(["ECON", "GUARD", "QUAL", "POLHOLD", "CON", "CAREGIVER", "PAT", "PROV", "AGNT"] as const).map((relation) => <option key={relation} value={relation}>{t(`forms.relatedPerson.relations.${relation}`)}</option>)}
            </Form.Select>
            <Form.Control.Feedback type="invalid">
                {t("forms.relatedPerson.invalidRelation")}
            </Form.Control.Feedback>
        </Form.Group>

        {props.displayButtons &&
            <Row className="mb-3">
                <Form.Group className="col">

                    <Button
                        className="button"
                        variant="danger"
                        onClick={onSubmit}
                    >
                        {t("forms.common.submit")}
                    </Button>
                    <Button
                        className="button"
                        variant="secondary"
                        onClick={onReset}
                    >
                        {t("forms.common.reset")}
                    </Button>
                </Form.Group>
            </Row>
        }
    </>;
};

export default RelatedPersonForm;
